import React from 'react';
import { 
  LayoutDashboard, RefreshCw, Users, Clock, 
  FileText, Database, Eye, FileSearch, Download, Code
} from 'lucide-react';
import LLMUsagePanel from '../LLMUsagePanel';
import EmailConfigPanel from '../EmailConfigPanel';
import DailySummarySubscription from '../DailySummarySubscription';
import EmailCommandsPanel from '../EmailCommandsPanel';
import LearningPanel from '../LearningPanel';
import SourceHealthPanel from '../SourceHealthPanel';
import CodeMetricsPanel from '../CodeMetricsPanel';
import CoherenceHistoryPanel from '../dashboard_tabs/admin/CoherenceHistoryPanel';

const AdminPanelTab = ({
  isLoadingAdmin,
  adminStats,
  adminSessions,
  adminReports,
  fetchAdminDashboard,
  exportPDF,
  token,
  user  // Added user prop for Learning panel
}) => {
  return (
    <div className="space-y-6">
      <h2 className="font-heading text-marine-cyan text-2xl uppercase tracking-wider flex items-center gap-3">
        <LayoutDashboard className="w-7 h-7" />
        Panneau d'Administration
      </h2>

      {isLoadingAdmin ? (
        <div className="p-8 text-center">
          <RefreshCw className="w-12 h-12 text-marine-cyan animate-spin mx-auto" />
          <p className="font-mono text-white/90 mt-4">Chargement des données...</p>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          {adminStats && (
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-marine-green/10 border border-marine-green p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-marine-green" />
                  <div>
                    <p className="font-mono text-white/80 text-xs uppercase">Utilisateurs</p>
                    <p className="font-heading text-marine-green text-3xl">{adminStats.total_users}</p>
                  </div>
                </div>
              </div>
              <div className="bg-marine-cyan/10 border border-marine-cyan p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-marine-cyan" />
                  <div>
                    <p className="font-mono text-white/80 text-xs uppercase">Sessions actives</p>
                    <p className="font-heading text-marine-cyan text-3xl">{adminStats.active_sessions}</p>
                  </div>
                </div>
              </div>
              <div className="bg-marine-amber/10 border border-marine-amber p-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-marine-amber" />
                  <div>
                    <p className="font-mono text-white/80 text-xs uppercase">Rapports</p>
                    <p className="font-heading text-marine-amber text-3xl">{adminStats.total_reports}</p>
                  </div>
                </div>
              </div>
              <div className="bg-marine-slate/20 border border-marine-slate p-4">
                <div className="flex items-center gap-3">
                  <Database className="w-6 h-6 text-white/90" />
                  <div>
                    <p className="font-mono text-white/80 text-xs uppercase">Sources actives</p>
                    <p className="font-heading text-white text-3xl">{adminStats.enabled_sources}/{adminStats.total_sources}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sessions */}
          <div className="bg-black border-2 border-marine-cyan/50">
            <div className="bg-marine-cyan/20 px-4 py-3 border-b border-marine-cyan/50 flex items-center justify-between">
              <span className="font-mono text-marine-cyan text-sm uppercase tracking-wider flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Connexions récentes (24h)
              </span>
              <button
                onClick={fetchAdminDashboard}
                className="text-marine-cyan hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {adminSessions.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-marine-slate/30">
                    <tr className="font-mono text-white/80 text-xs uppercase">
                      <th className="text-left p-3">Utilisateur</th>
                      <th className="text-left p-3">Grade</th>
                      <th className="text-left p-3">Connexion</th>
                      <th className="text-left p-3">Dernière activité</th>
                      <th className="text-center p-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-marine-slate/20">
                    {adminSessions.map((session, idx) => {
                      const loginTime = new Date(session.login_at);
                      const lastActivity = new Date(session.last_activity);
                      const isRecent = (Date.now() - lastActivity.getTime()) < 30 * 60 * 1000;
                      
                      return (
                        <tr key={idx} className="hover:bg-marine-slate/10">
                          <td className="p-3">
                            <p className="font-mono text-white text-sm">{session.user_name}</p>
                            <p className="font-mono text-white/80 text-xs">{session.user_email}</p>
                          </td>
                          <td className="p-3 font-mono text-marine-cyan text-sm">{session.user_rank}</td>
                          <td className="p-3 font-mono text-white/90 text-sm">
                            {loginTime.toLocaleString('fr-FR')}
                          </td>
                          <td className="p-3 font-mono text-white/90 text-sm">
                            {lastActivity.toLocaleString('fr-FR')}
                          </td>
                          <td className="p-3 text-center">
                            <span className={`inline-block w-3 h-3 rounded-full ${isRecent ? 'bg-marine-green animate-pulse' : 'bg-marine-slate'}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center">
                  <p className="font-mono text-white/80">Aucune session enregistrée</p>
                </div>
              )}
            </div>
          </div>

          {/* Reports */}
          <div className="bg-black border-2 border-marine-amber/50">
            <div className="bg-marine-amber/20 px-4 py-3 border-b border-marine-amber/50">
              <span className="font-mono text-marine-amber text-sm uppercase tracking-wider flex items-center gap-2">
                <FileSearch className="w-4 h-4" />
                Rapports générés ({(adminReports || []).length})
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-marine-slate/20">
              {(adminReports || []).map((report, idx) => (
                <div key={idx} className="p-4 hover:bg-marine-slate/10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-marine-cyan text-sm font-bold">{report.report_number}</span>
                        <span className="px-2 py-0.5 bg-marine-amber/20 text-marine-amber text-xs font-mono">v{report.version}</span>
                      </div>
                      <p className="font-mono text-white text-sm mb-2">{report.title}</p>
                      
                      {/* Brief/Prompt visible */}
                      {report.brief && (
                        <div className="bg-marine-slate/20 p-3 border-l-2 border-marine-cyan mb-2">
                          <p className="font-mono text-white/80 text-xs uppercase mb-1">Brief / Prompt</p>
                          <p className="font-mono text-white/80 text-xs line-clamp-3">{report.brief}</p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs">
                        <span className="font-mono text-white/80">
                          Par: <span className="text-marine-cyan">{report.created_by}</span>
                        </span>
                        <span className="font-mono text-white/80">
                          {new Date(report.created_at).toLocaleString('fr-FR')}
                        </span>
                        {report.scan_info && (
                          <span className="font-mono text-white/80">
                            {report.scan_info.source_count} sources
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => exportPDF(report.id)}
                      className="px-4 py-2 bg-marine-green/20 border border-marine-green text-marine-green hover:bg-marine-green hover:text-black font-mono text-xs uppercase flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                  </div>
                </div>
              ))}
              
              {(adminReports || []).length === 0 && (
                <div className="p-8 text-center">
                  <p className="font-mono text-white/80">Aucun rapport généré</p>
                </div>
              )}
            </div>
          </div>

          {/* LLM Usage Statistics */}
          <LLMUsagePanel token={token} />

          {/* Source Health Panel - v2.5.0 */}
          <SourceHealthPanel token={token} />

          {/* Code Metrics Panel - v2.6.0 */}
          <div className="bg-black border-2 border-marine-cyan/50">
            <div className="bg-marine-cyan/10 px-4 py-3 border-b border-marine-cyan/50">
              <span className="font-mono text-marine-cyan text-sm uppercase tracking-wider flex items-center gap-2">
                <Code className="w-4 h-4" />
                Métriques du Code v3.0
              </span>
            </div>
            <div className="p-4">
              <CodeMetricsPanel />
            </div>
          </div>

          {/* Email Configuration */}
          <EmailConfigPanel token={token} />

          {/* Daily Summary Subscription */}
          <DailySummarySubscription token={token} isAdmin={true} />

          {/* Email Commands Panel */}
          <EmailCommandsPanel token={token} />

          {/* Learning & Reconfiguration Panel */}
          <LearningPanel token={token} user={user} />

          {/* Documentation Technique */}
          <div className="bg-black border-2 border-purple-500/50">
            <div className="bg-purple-900/30 px-4 py-3 border-b border-purple-500/50">
              <span className="font-mono text-purple-400 text-sm uppercase tracking-wider flex items-center gap-2">
                <FileSearch className="w-4 h-4" />
                Documentation Technique
              </span>
            </div>
            <div className="p-4 grid md:grid-cols-4 gap-4">
              <a
                href="/api/static/AUDIT_TECHNIQUE.md"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-purple-900/20 border border-purple-500/30 hover:bg-purple-900/40 transition-colors"
                data-testid="download-audit-btn"
              >
                <Download className="w-6 h-6 text-purple-400" />
                <div>
                  <p className="font-mono text-white text-sm font-bold">Audit Technique</p>
                  <p className="font-mono text-white/80 text-xs">Architecture, endpoints, modules</p>
                </div>
              </a>
              <a
                href="/api/static/PRD.md"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-marine-cyan/20 border border-marine-cyan/30 hover:bg-marine-cyan/40 transition-colors"
                data-testid="download-prd-btn"
              >
                <Download className="w-6 h-6 text-marine-cyan" />
                <div>
                  <p className="font-mono text-white text-sm font-bold">PRD</p>
                  <p className="font-mono text-white/80 text-xs">Product Requirements</p>
                </div>
              </a>
              <a
                href="/api/static/RAPPORT_STABILISATION_v2.4.1.md"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-blue-900/20 border border-blue-500/30 hover:bg-blue-900/40 transition-colors"
                data-testid="download-stabilization-btn"
              >
                <Download className="w-6 h-6 text-blue-400" />
                <div>
                  <p className="font-mono text-white text-sm font-bold">Stabilisation v2.4.1</p>
                  <p className="font-mono text-white/80 text-xs">Rapport de phase</p>
                </div>
              </a>
              <a
                href="/api/docs/changelog"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-marine-amber/20 border border-marine-amber/30 hover:bg-marine-amber/40 transition-colors"
              >
                <FileText className="w-6 h-6 text-marine-amber" />
                <div>
                  <p className="font-mono text-white text-sm font-bold">Changelog</p>
                  <p className="font-mono text-white/80 text-xs">Historique des versions</p>
                </div>
              </a>
            </div>
          </div>

          {/* Coherence History Panel (v2.6.1) */}
          <CoherenceHistoryPanel token={token} />
        </>
      )}
    </div>
  );
};

export default AdminPanelTab;
